import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import CloseIcon from '@mui/icons-material/Close';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import Draggable from 'react-draggable';
import { PreviewProps } from 'common/models/Previews/Previews';
import {
  PREVIEW_HEIGHT,
  PREVIEW_TABLE_CONTENT,
  PREVIEW_TABLE_LABEL,
  PREVIEW_TABLE_STYLE,
  PREVIEW_TITLE_STYLE,
} from 'util/Definitions/Constants/Previews';
import { GetEmailAttachments, GetEmailMessageByActivityId } from 'services/MessagesService';
import { EmailMessage, MessageAttachment } from 'common/models/EmailIntegration';
import Grid2 from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { Editor } from '@tinymce/tinymce-react';
import DocumentPreviewDialog from 'components/Dialogs/Documents/DocumentPreviewDialog';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

const skeletonText = <Skeleton variant="text" height="40px" sx={{ minWidth: '300px' }} />;

const formatDate = (date: string | null) => {
  if (date) return moment(date).format('DD MMM YYYY h:mm A');
  return '';
};

const customDialogWidth = 1200;

export default function MessageQuickViewComponent({
  recordId,
  isSourceHovered,
  hideCallback,
  hideDelayMs = 1250,
}: PreviewProps) {
  const [shouldClose, setShouldClose] = useState(false);
  const [isFetchingMessage, setIsFetchingMessage] = useState(false);
  const [isFetchingAttachments, setIsFetchingAttachments] = useState(false);
  const [isFetchingFile, setIsFetchingFile] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [message, setMessage] = useState<EmailMessage>();
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  const [previewAttachmentId, setPreviewAttachmentId] = useState(0);

  useEffect(() => {
    const getData = async () => {
      setIsFetchingMessage(true);
      const res = await GetEmailMessageByActivityId(recordId);
      if (res) setMessage(res);
      setIsFetchingMessage(false);
    };
    getData();
  }, [recordId]);

  useEffect(() => {
    const getAttachments = async () => {
      setIsFetchingAttachments(true);
      const res = await GetEmailAttachments(recordId);
      if (res) setAttachments(res);
      setIsFetchingAttachments(false);
    };
    message && message.attachments > 0 && getAttachments();
  }, [message, recordId]);

  useEffect(() => {
    if (shouldClose) {
      const hidePreviewTimeout = setTimeout(() => {
        hideCallback();
      }, hideDelayMs);
      return () => clearTimeout(hidePreviewTimeout);
    }
  }, [shouldClose, hideDelayMs, hideCallback]);

  useEffect(() => {
    if (isSourceHovered || isHovered || previewAttachmentId) setShouldClose(false);
    else setShouldClose(true);
  }, [isSourceHovered, isHovered, previewAttachmentId]);

  const renderedAttachments = useMemo(() => {
    if (attachments && attachments.length > 0) {
      return attachments.map((a) => (
        <Chip
          size="small"
          key={a.id}
          label={a.documentName}
          onClick={() => setPreviewAttachmentId(a.id)}
          sx={{ mr: '10px', cursor: 'pointer' }}
        />
      ));
    }
    return (
      <Typography component="span" variant="body2">
        None
      </Typography>
    );
  }, [attachments]);

  return (
    <>
      <Backdrop open={isFetchingFile} sx={{ zIndex: (t) => t.zIndex.modal + 1 }}>
        <CircularProgress sx={{ color: 'text.disabled' }} />
      </Backdrop>
      <DocumentPreviewDialog
        open={Boolean(previewAttachmentId)}
        source="message-attachments"
        closeHandler={() => setPreviewAttachmentId(0)}
        documentId={previewAttachmentId}
        loadingHandler={setIsFetchingFile}
      />
      <Draggable
        handle="#draggable-dialog-title"
        cancel={'[class*="MuiDialogContent-root"]'}
        defaultPosition={{
          x: window.innerWidth / 2 - customDialogWidth / 2,
          y: window.innerHeight / 2 - PREVIEW_HEIGHT / 2,
        }}
      >
        <Dialog
          open={true}
          maxWidth={false}
          slotProps={{
            paper: {
              onMouseEnter: () => setIsHovered(true),
              onMouseLeave: () => setIsHovered(false),
              sx: { height: `${PREVIEW_HEIGHT}px`, width: `${customDialogWidth}px` },
            },
          }}
          hideBackdrop
          sx={{ bottom: 'unset', right: 'unset', left: 'unset' }}
        >
          <DialogTitle component="div" display="flex" sx={PREVIEW_TITLE_STYLE}>
            <div style={{ alignSelf: 'center', flexGrow: 1 }}>
              {isFetchingMessage || !message ? skeletonText : message?.subject}
            </div>
            <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
              <IconButton
                id="draggable-dialog-title"
                sx={{ cursor: 'move', color: (t) => t.palette.primary.contrastText }}
              >
                <ControlCameraIcon />
              </IconButton>
              <IconButton
                onClick={() => hideCallback()}
                sx={{ color: (t) => t.palette.primary.contrastText }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex' }}>
            <div
              style={{ paddingTop: '10px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}
            >
              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <table style={PREVIEW_TABLE_STYLE}>
                    <tbody>
                      <tr>
                        <td width="30%" style={PREVIEW_TABLE_LABEL}>
                          Sent
                        </td>
                        <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                          {isFetchingMessage || !message
                            ? skeletonText
                            : formatDate(message.messageDate)}
                        </td>
                      </tr>
                      <tr>
                        <td width="30%" style={PREVIEW_TABLE_LABEL}>
                          From
                        </td>
                        <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                          {isFetchingMessage || !message ? skeletonText : message.from}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Grid2>
                <Grid2 size={6}>
                  <table style={PREVIEW_TABLE_STYLE}>
                    <tbody>
                      <tr>
                        <td width="30%" style={PREVIEW_TABLE_LABEL}>
                          To
                        </td>
                        <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                          {isFetchingMessage || !message ? skeletonText : message.to}
                        </td>
                      </tr>
                      <tr>
                        <td width="30%" style={PREVIEW_TABLE_LABEL}>
                          CC
                        </td>
                        <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                          {isFetchingMessage || !message ? skeletonText : message.cc}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Grid2>
              </Grid2>
              <Grid2 container spacing={2}>
                <Grid2 size={12}>
                  <table style={PREVIEW_TABLE_STYLE}>
                    <tbody>
                      <tr>
                        <td width="15%" style={PREVIEW_TABLE_LABEL}>
                          Attachments
                        </td>
                        <td width="85%" style={PREVIEW_TABLE_CONTENT}>
                          {isFetchingAttachments || !attachments
                            ? skeletonText
                            : renderedAttachments}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Grid2>
              </Grid2>
              <div style={{ paddingTop: '5px' }}>
                <Divider />
              </div>
              {isFetchingMessage || !message ? (
                <Skeleton variant="rectangular" height="100%" />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flex: '1',
                    flexDirection: 'column',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flex: '1',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        width: '100%',
                        overflow: 'auto',
                      }}
                    >
                      <Editor
                        disabled
                        tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce-5.10.2/tinymce.min.js'}
                        value={message.body}
                        init={{
                          height: '100%',
                          branding: false,
                          menubar: false,
                          toolbar: false,
                          statusbar: false,
                          contextmenu: false,
                          resize: false,
                          browser_spellcheck: true,
                          forced_root_block: false,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </Draggable>
    </>
  );
}
